/** One tenant's values aligned to the chart's categories (one number per month, in order). */
export interface TenantSeriesValues {
  name: string;
  values: number[];
}

/** A stacked-chart series: a single tenant, or the aggregated "Other" remainder carrying its members. */
export interface TenantStackSeries {
  name: string;
  values: number[];
  /** For the "Other" series only: the tenants folded into it, so the chart can break it down on hover. */
  members?: TenantSeriesValues[];
}

export const OTHER_SERIES_NAME = 'Other';

/**
 * Ranks tenants by their latest-month value and returns the top `topN` as their own series, folding
 * every remaining tenant into a single "Other" series that carries its members (so a chart can break
 * "Other" down on hover). Tenants with nothing across the window are dropped, and "Other" is omitted
 * when it would be all zero. Mirrors the backend StorageByTenantBuilder.BuildTopN so the usage
 * drill-down charts fold the same way as the featured storage chart.
 */
export function buildTopNStack(series: TenantSeriesValues[], topN = 8): TenantStackSeries[] {
  const categoryCount = series.reduce((max, s) => Math.max(max, s.values.length), 0);
  const windowTotal = (s: TenantSeriesValues) => s.values.reduce((sum, v) => sum + (v || 0), 0);
  const latest = (s: TenantSeriesValues) => (categoryCount > 0 ? s.values[categoryCount - 1] || 0 : 0);

  const active = series
    .filter((s) => windowTotal(s) > 0)
    .sort(
      (a, b) =>
        latest(b) - latest(a) ||
        windowTotal(b) - windowTotal(a) ||
        a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
    );

  const n = Math.max(topN, 0);
  const top = active.slice(0, n);
  const rest = active.slice(n);

  const result: TenantStackSeries[] = top.map((s) => ({ name: s.name, values: s.values.slice() }));

  if (rest.length) {
    const otherValues = Array.from({ length: categoryCount }, (_, i) =>
      rest.reduce((sum, s) => sum + (s.values[i] || 0), 0)
    );

    if (otherValues.some((v) => v > 0)) {
      result.push({
        name: OTHER_SERIES_NAME,
        values: otherValues,
        members: rest.map((s) => ({ name: s.name, values: s.values.slice() })),
      });
    }
  }

  return result;
}
